/**
 * RD_DD_Autism.js — Autism Diagnostic Decision (MDT Review) Review Document
 *
 * Clinician-facing paragraph review page for the Autism DD module. It mirrors
 * RD_SSI_Autism.js (same modular subcollection architecture, the same
 * approve/edit/reset/skip transactions on reportContent/diagnosticDecision, the
 * same exit-approval flow) but with a DD-specific, partly-dynamic document
 * structure.
 *
 * ─── DOCUMENT STRUCTURE ──────────────────────────────────────────────────────
 *
 * The DD report is composed of up to three sections:
 *
 *   1. "Diagnostic Decision" — a SINGLE deterministic primary-diagnosis sentence
 *      (asd_dd_primaryDiagnosisSentence) driven by the Met/Not-met outcome.
 *
 *   2. "Decisions for Other Conditions" — one paragraph per emerging differential
 *      (asd_dd_secondaryCondition_{N}_sentence). The condition list is the
 *      de-duplicated UNION of the SSI + OBS review differentials, sorted
 *      alphabetically so condition N here maps to the same _{N}_ key the backend
 *      generated (it iterates the conditions in sorted order). This section is
 *      omitted entirely when there are no differentials.
 *
 *   3. "Final Formulation" — the LLM final-formulation paragraph
 *      (asd_dd_finalFormulation).
 *
 * Paragraphs with no generated output are hidden by the Section component and are
 * not counted by useModularApprovalTracking, so a partially-generated report
 * reviews cleanly.
 *
 * Data source, approval tracking, Firestore writes, navigation, and the absence
 * of PDF rendering / completion emails all match RD_SSI_Autism.js — see that
 * file for the architectural rationale shared across the Autism RD pages.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useModularApprovalTracking } from '../../../hooks/useModularApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeModularSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { toast } from 'react-toastify';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { renderModelInput } from '../../../utils/renderModelInput';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { STAGE_TO_CLINICIAN_KEY } from '../../../constants/adultAutismStages';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { TriggerRenderAutismDocTemplate } from '../../../utils/TriggerRenderAutismDocTemplate';
import { rerenderAutismPatientReportIfStale } from '../../../utils/autoAutismPatientReport';
import { db, auth } from '../../../firebase';
import { doc as firestoreDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { getPatientReportContentPathSegments } from '../../../constants/adultAutismFirestorePaths';
import { getCombinedEmergingDifferentials } from '../../../utils/autismDDRouting';
import './styles/ReviewDocuments.css';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';

// ─── STATIC SECTION CONSTANTS ─────────────────────────────────────────────────
// The primary-decision and final-formulation codes are fixed. The middle
// "other conditions" section is built dynamically from the differentials union
// (see docStructure below). Exported for independent testing.

export const ASD_DD_PRIMARY_CODE = 'asd_dd_primaryDiagnosisSentence';
export const ASD_DD_FORMULATION_CODE = 'asd_dd_finalFormulation';

export const ASD_DD_PRIMARY_SECTION = {
  'Diagnostic Decision': {
    '1': { title: 'Autism Spectrum Disorder (ASD)', code: ASD_DD_PRIMARY_CODE },
  },
};

export const ASD_DD_FORMULATION_SECTION = {
  'Final Formulation': {
    '0': { title: 'Final Formulation', code: ASD_DD_FORMULATION_CODE },
  },
};

/**
 * Builds the per-other-condition section from the differentials union.
 * Mirrors the ADHD RD_DD numbering: conditions are sorted alphabetically and the
 * Nth condition (1-based) maps to `asd_dd_secondaryCondition_{N}_sentence`.
 *
 * @param {string[]} conditions - de-duplicated differentials union (any order)
 * @returns {Object} section map keyed "0","1",... or {} when there are none
 */
export function buildAsdDDOtherConditionsSection(conditions) {
  const sorted = [...(conditions || [])].sort();
  if (sorted.length === 0) return {};
  const section = {};
  sorted.forEach((name, i) => {
    section[i.toString()] = {
      title: `Decision for ${name}`,
      code: `asd_dd_secondaryCondition_${i + 1}_sentence`,
    };
  });
  return section;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function ReviewDocumentDD_Autism() {
  const { patientId } = useParams();
  const { claims } = useAuth();
  const navigate = useNavigate();

  // MODULE_PREFIX_CODE is the Firestore field prefix for the dd* approval flags
  // (ddReportApproved, ddReportFirstApprovedAt, ...). MODULE_TITLE is shown in
  // the review header ("Reviewing: MDT Review").
  const MODULE_TITLE = 'MDT Review';
  const MODULE_PREFIX_CODE = 'dd';

  const {
    header,
    assessmentStages,
    reportContent,
    clearPatientData,
    fetchPatientData,
    refreshReportContent,
    loadedPatientId,
  } = useContext(ModularPatientDataContext);

  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // ─── Edit permission gate ───────────────────────────────────────────────────
  // Only the lead clinician assigned as primary for the Diagnostic Decision
  // module may review, edit, and approve these AI paragraphs. Everyone else who
  // can open the Documents Dashboard — including DD *supporting* (MDT) clinicians
  // who can view the page — lands here read-only: isClinicianAssignedToStage
  // deliberately excludes supporting clinicians from edit rights. Defence in
  // depth behind the dashboard's assignee-gated Review button so a deep link
  // cannot become an editing back door. Reads the same
  // `header.clinicians.clinicianDD` slot the dashboard uses.
  const canEdit = useCanEditStage(header, 'DD');
  const assignedClinicianName =
    header?.clinicians?.[STAGE_TO_CLINICIAN_KEY.diagnosticDecision]?.clinicianName;

  // ─── Derived data from the DD reportContent document ────────────────────────
  const ddData = reportContent?.diagnosticDecision || {};
  const REPORT_DATA_OUTPUTS = useMemo(() => ddData.outputs || {}, [ddData.outputs]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    ddData.editHistory || {},
    [ddData.editHistory]);
  const REPORT_DATA_INPUTS = useMemo(() => ddData.inputs || {}, [ddData.inputs]);
  const REPORT_DATA_APPROVED = useMemo(() => ddData.approvedParagraphs || {}, [ddData.approvedParagraphs]);

  // ─── Document structure (partly dynamic) ────────────────────────────────────
  // Primary decision + (optional) per-condition decisions + final formulation.
  // The other-conditions list comes from the SSI+OBS differentials union so it
  // always matches what the backend generated, even before all outputs land.
  const conditionsUnion = useMemo(
    () => getCombinedEmergingDifferentials(assessmentStages),
    [assessmentStages],
  );

  const docStructure = useMemo(() => {
    const structure = { ...ASD_DD_PRIMARY_SECTION };
    const otherSection = buildAsdDDOtherConditionsSection(conditionsUnion);
    if (Object.keys(otherSection).length > 0) {
      structure['Decisions for Other Conditions'] = otherSection;
    }
    return { ...structure, ...ASD_DD_FORMULATION_SECTION };
  }, [conditionsUnion]);

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    clinicianInputs: null,
    clinicianDecisions: null,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showInitialWarning, setShowInitialWarning] = useState(true);
  const [initialContent, setInitialContent] = useState({});
  const [preEditContent, setPreEditContent] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  // ─── Approval tracking via modular hook ─────────────────────────────────────
  const {
    totalParagraphs,
    reviewedCodes,
    isFullyApproved,
    showWarning,
    addReviewedCode,
    removeReviewedCode,
  } = useModularApprovalTracking({
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
    reportDataApproved: REPORT_DATA_APPROVED,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // ─── Snapshot initial content on first load ─────────────────────────────────
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS && Object.keys(REPORT_DATA_OUTPUTS).length > 0) {
      const initialValues = {};
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== '') {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // ─── Refs and floating panel ────────────────────────────────────────────────
  const documentContainerRef = useRef(null);
  const editableContentRef = useRef(null);

  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  const getDocRef = useCallback(() => {
    const reportContentPath = getPatientReportContentPathSegments(patientId, 'diagnosticDecision');
    return firestoreDoc(db, ...reportContentPath);
  }, [patientId]);

  // ─── Paragraph click handler ────────────────────────────────────────────────
  // Unlike the SSI page, we tolerate a missing inputs entry: the deterministic
  // primary-diagnosis sentence may have no AI inputs, but the clinician must
  // still be able to select and approve it. Missing inputs simply render an
  // empty AI Inputs panel.
  const handleParagraphClick = (event, code, content) => {
    setIsEditing(false);

    const inputs = REPORT_DATA_INPUTS?.[code] || {};
    setState(prevState => ({
      ...prevState,
      selectedCode: code,
      textareaContent: content || ' ',
      clinicianInputs: inputs['Clinician Notes'],
      clinicianDecisions: inputs['Clinician Decisions'],
      isSidePanelOpen: true,
    }));

    setTimeout(() => {
      safeUpdatePosition(code);
    }, 0);
  };

  // ─── Section component ──────────────────────────────────────────────────────
  const Section = ({ title, paragraphs }) => {
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ''
    );

    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1 text-blue-500">{title}</h2>
        <div className="mb-3" />

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = REPORT_DATA_OUTPUTS[code];
          const decodedContent = decodeHtmlEntities(content);
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                  relative text-sm font-medium p-2 rounded-lg
                  transition-all duration-200
                  ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20'
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                  ${state.selectedCode === code ? 'ring ring-violet-800' : ''}
                  ${!isEditing ? 'cursor-pointer' : ''}
                `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');
                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Edit handlers ──────────────────────────────────────────────────────────

  const handleStartEdit = () => {
    if (!canEdit) return;
    if (state.selectedCode) {
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${CSS.escape(state.selectedCode)} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  const handleSaveEdit = () => {
    if (!canEdit) {
      toast.error('Only the lead clinician assigned to the Diagnostic Decision can edit these paragraphs.');
      return;
    }
    if (!state.selectedCode) return;

    const spanCode = state.selectedCode;
    const newValue = REPORT_DATA_OUTPUTS[spanCode];
    const previousValue = initialContent[spanCode] || '';

    if (newValue !== previousValue) {
      removeReviewedCode(state.selectedCode);
      setIsEditing(false);
      safeUpdatePosition(state.selectedCode);

      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        editType: 'manual',
      };
      const timestampKey = Date.now().toString();

      // Targeted per-key updateDoc (root fields on this stage's reportContent
      // subcollection doc) instead of a read-modify-write runTransaction. The
      // transaction's document-version precondition contends under slow 3G/4G
      // and silently discards the edit; a plain updateDoc on distinct field
      // paths does not. See handleParagraphApproval for the full rationale.
      const docRef = getDocRef();

      const startedAt = performance.now();
      updateDoc(docRef, {
        // Persist the edited text
        [`outputs.${spanCode}`]: newValue,
        // Editing invalidates any prior approval
        [`approvedParagraphs.${spanCode}`]: deleteField(),
        // Append an audit entry under this paragraph's edit history
        [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      }).then(() => {
        // Confirms the edit persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
        // Analytics mirror of the edit that just persisted: paragraph code,
        // section and the two lengths only — never the wording itself.
        emitParagraphEdited({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
          prevText: previousValue,
          priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
          nextText: newValue,
        });
      }).catch(error => {
        console.error('Error updating paragraph approval on edit:', error);
        // Roll back the optimistic un-approval and surface the failure so
        // the clinician does not believe their edit was saved.
        addReviewedCode(state.selectedCode);
        toast.error(
          'Your edit did not save. Please try editing the paragraph again.',
          { toastId: 'paragraph-edit-save-error' }
        );
      });

      setSectionDirty(true);
    } else {
      setIsEditing(false);
      safeUpdatePosition(state.selectedCode);
    }
  };

  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  const handleReset = () => {
    if (!canEdit) {
      toast.error('Only the lead clinician assigned to the Diagnostic Decision can reset these paragraphs.');
      return;
    }
    if (!state.selectedCode || !initialContent[state.selectedCode]) return;

    const spanCode = state.selectedCode;
    const newValue = initialContent[spanCode];
    const previousValue = REPORT_DATA_OUTPUTS[spanCode];

    if (REPORT_DATA_OUTPUTS) {
      REPORT_DATA_OUTPUTS[spanCode] = newValue;
    }
    removeReviewedCode(spanCode);

    const editEntry = {
      clinicianId: auth.currentUser.uid,
      clinicianName: auth.currentUser.displayName,
      timestamp: serverTimestamp(),
      previousValue,
      newValue,
      isReset: true,
    };
    const timestampKey = Date.now().toString();

    // Targeted per-key updateDoc (root fields) instead of a read-modify-write
    // runTransaction, which contends on the doc version under slow 3G/4G and
    // loses the reset. See handleSaveEdit / handleParagraphApproval.
    const docRef = getDocRef();

    const startedAt = performance.now();
    updateDoc(docRef, {
      // Restore the original generated text
      [`outputs.${spanCode}`]: newValue,
      // A reset also invalidates any prior approval
      [`approvedParagraphs.${spanCode}`]: deleteField(),
      // Record the reset in this paragraph's edit history
      [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
      // Review metadata (mirrors the approval write)
      lastReviewedAt: serverTimestamp(),
      reviewedBy: auth.currentUser.uid,
      reviewedByName: auth.currentUser.displayName,
    }).then(() => {
      // Confirms the reset persisted AND the paragraph was un-approved
      // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
      // Analytics mirror of the reset that just persisted (code only).
      emitParagraphReset({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code: spanCode,
      });
    }).catch(error => {
      console.error('Error resetting paragraph:', error);
      // Roll back both content and approval, then surface the failure so the
      // clinician knows the reset did not persist.
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[spanCode] = previousValue;
      }
      addReviewedCode(spanCode);
      toast.error(
        'The paragraph reset did not save. Please try again.',
        { toastId: 'paragraph-reset-save-error' }
      );
    });

    setIsEditing(false);
    safeUpdatePosition(spanCode);
    setSectionDirty(true);
  };

  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  // ─── Paragraph approval ─────────────────────────────────────────────────────
  const handleParagraphApproval = async (code, isApproving) => {
    if (!canEdit) {
      toast.error('Only the lead clinician assigned to the Diagnostic Decision can approve these paragraphs.');
      return;
    }
    const docRef = getDocRef();

    try {
      // Targeted per-key write instead of a full read-modify-write transaction
      // on approvedParagraphs — avoids document-version contention when a
      // clinician approves many paragraphs quickly on slow 3G/4G links (see
      // RD_CI.js for the full rationale). `code` is a dot-free identifier and
      // the fields below are siblings, so there is no conflicting field-path
      // rejection.
      const updateData = {
        [`approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      };

      // Timed, PII-free confirmation log (updateDoc resolves on server ack).
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'autism',
        isApproving,
      });

      setSectionDirty(true);
    } catch (error) {
      console.error('Error updating paragraph approval:', error);
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // reports finalising with missing / unapproved content.
      throw error;
    }
  };

  // ─── Mark as Done (approve + advance) ──────────────────────────────────────
  const handleMarkAsDone = async () => {
    if (!canEdit) {
      toast.error('Only the lead clinician assigned to the Diagnostic Decision can approve these paragraphs.');
      return;
    }
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (!paragraphElement) return;

    try {
      // Track the in-flight write so Save & Exit awaits it before finalising.
      trackApproval(
        handleParagraphApproval(state.selectedCode, true).catch(error => {
          console.error('Error in handleParagraphApproval:', error);
          // Roll back the optimistic update and tell the clinician — a
          // silently failed approval is exactly how incomplete sections
          // used to slip through to the rendered report.
          removeReviewedCode(state.selectedCode);
          toast.error(
            'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
            { toastId: 'paragraph-approval-save-error' }
          );
        })
      );

      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      addReviewedCode(state.selectedCode);

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();
        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;
          const inputs = REPORT_DATA_INPUTS[nextCode] || {};
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            clinicianInputs: inputs['Clinician Notes'],
            clinicianDecisions: inputs['Clinician Decisions'],
          }));
          setTimeout(() => { safeUpdatePosition(nextCode); }, 0);
        }, 300);
      } else {
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
          clinicianDecisions: null,
        }));
      }
    } catch (error) {
      console.error('Error in handleMarkAsDone:', error);
    }
  };

  // ─── Skip (advance without approving) ──────────────────────────────────────
  const handleSkip = () => {
    if (!canEdit) return;
    if (!state.selectedCode) return;

    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
    const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
      index > currentIndex && !reviewedCodes.has(p.id)
    );

    if (nextUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = nextUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        const nextCode = nextUnreviewed.id;
        const inputs = REPORT_DATA_INPUTS[nextCode] || {};
        setState(prev => ({
          ...prev,
          selectedCode: nextCode,
          isSidePanelOpen: true,
          clinicianInputs: inputs['Clinician Notes'],
          clinicianDecisions: inputs['Clinician Decisions'],
        }));
        setTimeout(() => { safeUpdatePosition(nextCode); }, 0);
      }, 300);
    } else {
      setState(prev => ({
        ...prev,
        selectedCode: null,
        isSidePanelOpen: false,
        clinicianInputs: null,
        clinicianDecisions: null,
      }));
    }
  };

  // ─── Go to first un-reviewed ───────────────────────────────────────────────
  const goToFirstUnreviewed = () => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  // ─── Exit review ───────────────────────────────────────────────────────────
  // Writes the dd* approval-status fields and navigates to the documents
  // dashboard. No PDF rendering / completion email (parity with RD_SSI_Autism).
  const handleExitReview = async () => {
    // Read-only viewers (not the DD lead, including DD supporting/MDT clinicians)
    // can leave the page, but must never run the finalise path: no approval/status
    // writes, no audit entry, and no PDF/patient-report render. Just drop cached
    // data and navigate back to the dashboard.
    if (!canEdit) {
      // Never act on assignment until the patient doc has loaded for THIS
      // patient. canEdit starts false and only becomes true once `header`
      // hydrates, so a legitimate assignee who clicks before that would
      // otherwise be treated as read-only and navigated away without
      // finalising. Once loaded, canEdit is authoritative.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        return;
      }
      clearPatientData();
      navigate(`/patient/${patientId}/adult-autism/documents`);
      return;
    }

    setIsSaving(true);

    try {
      // Guard: never finalise against a document that has not fully loaded for
      // THIS patient. This matters especially for DD because its docStructure is
      // partly DYNAMIC — the "other conditions" section is built from the
      // SSI+OBS differentials union in assessmentStages. If those stages have not
      // loaded, the denominator shrinks and we could write a false "approved"
      // flag with unapproved conditions behind it. Bail out and let the
      // clinician retry once loaded.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        setIsSaving(false);
        return;
      }

      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh document snapshot. This replaces writing the client's optimistic
      // isFullyApproved, which could diverge from Firestore. First-approval is
      // likewise snapshot-derived (write-once, correct across re-approvals).
      const { isFullyApproved: approvedNow, outstandingCodes, wasFirstApproval } =
        await finalizeModularSectionReview({
          patientId,
          stageKey: 'diagnosticDecision',
          flagPrefix: MODULE_PREFIX_CODE,
          docStructure,
        });

      // Reconcile the optimistic UI against the authoritative snapshot. Stay on
      // the page when phantom approvals exist (green on screen but the write
      // failed) or when the snapshot found outstanding content.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await refreshReportContent(patientId, 'diagnosticDecision');
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: wasFirstApproval,
      });

      // Kick off PDF/DOCX rendering of the DD report when all paragraphs are
      // approved (snapshot-derived) and something changed this session.
      if (approvedNow && sectionDirty) {
        TriggerRenderAutismDocTemplate({
          patientId,
          stageKey: 'diagnosticDecision',
          clinicianId: auth.currentUser.uid,
        }).catch((err) => console.error('Error triggering autism DD render:', err));
      }

      // Auto-finalise the combined Patient Report. Called unconditionally (fire-and-forget):
      // the helper reads fresh Firestore state and only (re)renders when all five section
      // reports are approved AND the patient report is stale — mirroring the ADHD flow, so
      // there is no manual "Generate" button. Safe even on a no-op exit.
      rerenderAutismPatientReportIfStale({ patientId, clinicianId: auth.currentUser.uid })
        .catch((err) => console.error('Autism PR auto-finalise error:', err));

      clearPatientData();
      setSectionDirty(false);
      navigate(`/patient/${patientId}/adult-autism/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Progress calculation ──────────────────────────────────────────────────
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // ─── Side effects: body overflow lock ──────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ─── Side effects: scroll during edit mode ─────────────────────────────────
  useEffect(() => {
    const currentContainer = documentContainerRef.current;
    if (isEditing && currentContainer) {
      const originalOverflow = currentContainer.style.overflow;
      currentContainer.style.overflow = 'auto';

      const handleWheel = (e) => { e.stopPropagation(); };
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  // ─── Side effects: deselect on background click ────────────────────────────
  const handlePageClick = useCallback((event) => {
    if (isEditing) return;
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false,
    }));
  }, [isEditing]);

  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) {
      container.addEventListener('click', handlePageClick);
    }
    return () => {
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // Keep claims/sectionDirty referenced to mirror RD_SSI_Autism's signature
  // without triggering unused-variable lints (sectionDirty drives future
  // unsaved-changes guards; claims is available for role-based UI parity).
  void claims;

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden">

        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-200"
            style={{ pointerEvents: 'none' }}
          />
        )}

        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />
          {!canEdit && header && (
            <div className="mt-2 bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700">
              Read-only view. Only{assignedClinicianName ? ` ${assignedClinicianName}, the lead clinician` : ' the lead clinician'} assigned to the Diagnostic Decision can review, edit, and approve these paragraphs. You can still preview and download the report from the Documents dashboard.
            </div>
          )}
        </div>

        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            <div className="flex h-full">
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={canEdit && Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                  canDelete={false}
                  onDelete={() => {}}
                />
              </div>

              <div className={`
                w-2/5 p-4 flex flex-col
                ${!state.selectedCode ? 'hidden' : ''}
              `}>
                <div className={`
                  flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto
                  border shadow-sm
                  flex flex-col h-full
                `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput('Your decisions', state.clinicianDecisions)}
                          {renderModelInput('Your notes', state.clinicianInputs)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
